import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import {
  FiArrowLeft,
  FiExternalLink,
  FiClock,
  FiCpu,
  FiShield,
  FiStar,
  FiZap,
  FiRepeat,
  FiSend,
} from 'react-icons/fi'
import {
  getCard,
  getCardImages,
  getUserByWallet,
  getNotifications,
  getLabUsersForVisibility,
  setCardTestVisibility,
  createLabCustodyClaim,
  getMyLabCustodyClaims,
  withdrawLabCustodyClaim,
} from '../services/api'
import {
  formatCardStatus,
  formatOrderStatus,
  formatGrade,
  formatOrderFinalGrade,
  formatDate,
  formatAddress,
  formatCurrency,
  safeDisplayValue,
  getIPFSUrl,
  normalizeIpfsDisplayUrl,
  getPolygonscanUrl,
  getPolygonscanTokenUrl,
  getOpenSeaUrl,
  getMintContractAddressForCard,
  isTestnet,
} from '../utils/formatters'
import LoadingSpinner from '../components/LoadingSpinner'
import { ImageLightboxProvider, useImageLightboxOptional } from '../components/ImageLightboxContext'
import RouteErrorBoundary from '../components/RouteErrorBoundary'
import GradingHistoryTimeline from '../components/GradingHistoryTimeline'
import CardDetailMediaGallery, { collectCardMedia } from '../components/CardDetailMediaGallery'
import CardRecordSections from '../components/CardRecordSections'
import PlateHeader from '../components/m1nt/PlateHeader'
import Holo3DCard from '../components/m1nt/card/Holo3DCard'
import CardAttributesLedger from '../components/CardAttributesLedger'
import AuthenticationSeal from '../components/m1nt/card/AuthenticationSeal'
import SealEmblem, { PolygonMark } from '../components/m1nt/card/SealEmblem'
import CardSealBadge from '../components/m1nt/card/CardSealBadge'
import CardAuthLegend from '../components/m1nt/card/CardAuthLegend'
import MediaLightbox from '../components/m1nt/card/MediaLightbox'
import ShootSection from '../components/m1nt/card/ShootSection'
import VideoSection from '../components/m1nt/card/VideoSection'
import { getChainName, ON_CHAIN_SECTION_ID } from '../components/m1nt/card/cardFields'
import { getFlowConfig, TIMELINE_STEPS } from '../utils/cardFlowConfig'
import { buildTimelineDates } from '../utils/orderTimelineDates'
import { getOwnerCardDisplayName } from '../utils/ownerCardDisplay'
import CardFlowModal from '../components/CardFlowModal'
import CardEntryTransition from '../components/CardEntryTransition'
import BurnedCardPlaceholder from '../components/BurnedCardPlaceholder'
import { useAuthSession } from '../contexts/AuthSessionContext'

function orderNeedsOwnerAction(order) {
  return !!getFlowConfig(order?.status)?.ownerAction
}

/** GCS-only attribute paths — gallery shows files via signed URLs */
function isHiddenAttributeKey(key) {
  const k = String(key).toLowerCase()
  return [
    'quotevideos',
    'quotedocuments',
    'ownerstorygcsurl',
    'ownerdeclarationgcsurl',
    'labcustodymint',
    'montonchainholder',
    'graderreferencelinks',
    'set',
    'year',
    'rarity',
    'number',
    'ownerstory',
  ].includes(k)
}

/** Lab removed owner submission front/back from NFT — must not show in owner gallery. */
function isOwnerOriginalExcluded(card, side) {
  const excluded = card?.mintMediaExclusions?.originalImages
  if (Array.isArray(excluded) && excluded.includes(side)) return true
  if (side === 'front' && card?.frontImageUrl === null) return true
  if (side === 'back' && card?.backImageUrl === null) return true
  return false
}

const VIEW_CHAIN_ID = parseInt(import.meta.env.VITE_CHAIN_ID || '80002', 10)

function CardDetailContent({ id }) {
  const imageLightbox = useImageLightboxOptional()
  const { wallet, user } = useAuthSession()
  const navigate = useNavigate()
  const location = useLocation()
  const [card, setCard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [imageUrls, setImageUrls] = useState({ frontImageUrl: null, backImageUrl: null })
  const [loadingImages, setLoadingImages] = useState(false)
  const [_user, setUser] = useState(null)
  const [notifEntryTransition, setNotifEntryTransition] = useState(
    () => !!location.state?.fromNotification
  )
  const [flowModalOpen, setFlowModalOpen] = useState(false)
  const [cardOpen, setCardOpen] = useState(false)
  const [flowModalNotifications, setFlowModalNotifications] = useState([])
  const [testModalOpen, setTestModalOpen] = useState(false)
  const [labVisibilityUsers, setLabVisibilityUsers] = useState([])
  const [testEnabled, setTestEnabled] = useState(false)
  const [testSelectedWallets, setTestSelectedWallets] = useState([])
  const [savingTestVisibility, setSavingTestVisibility] = useState(false)
  const [testVisibilityError, setTestVisibilityError] = useState('')
  const [labCustodyModalOpen, setLabCustodyModalOpen] = useState(false)
  const [labCustodyWallet, setLabCustodyWallet] = useState('')
  const [labCustodyBusy, setLabCustodyBusy] = useState(false)
  const [labCustodyErr, setLabCustodyErr] = useState('')
  const [myLabCustodyClaim, setMyLabCustodyClaim] = useState(null)
  const notifTransitionHandledRef = useRef(false)
  const notifTransitionStartRef = useRef(location.state?.fromNotification ? Date.now() : null)

  useEffect(() => {
    const from = !!location.state?.fromNotification
    setNotifEntryTransition(from)
    notifTransitionHandledRef.current = false
    notifTransitionStartRef.current = from ? Date.now() : null
  }, [id, location.state?.fromNotification])

  useEffect(() => {
    loadCard()
  }, [id])

  useEffect(() => {
    if (wallet) {
      getUserByWallet(wallet)
        .then((res) => setUser(res.data.data))
        .catch(() => {})
    } else {
      setUser(null)
    }
  }, [wallet])

  // Load signed image URLs when card is loaded
  useEffect(() => {
    if (card?.id) {
      loadCardImages()
    }
  }, [card?.id])

  useEffect(() => {
    const ownerId = user?.id || _user?.id
    if (!card?.id || !ownerId || card.owner?.id !== ownerId) {
      setMyLabCustodyClaim(null)
      return
    }
    if (card.status !== 'MINTED' || !card.attributes?.labCustodyMint) {
      setMyLabCustodyClaim(null)
      return
    }
    let cancelled = false
    getMyLabCustodyClaims()
      .then((res) => {
        if (cancelled) return
        const list = res.data?.data || []
        const hit = list.find((r) => r.cardId === card.id && r.status === 'PENDING_LAB')
        setMyLabCustodyClaim(hit || null)
      })
      .catch(() => {
        if (!cancelled) setMyLabCustodyClaim(null)
      })
    return () => {
      cancelled = true
    }
  }, [
    card?.id,
    card?.owner?.id,
    card?.status,
    card?.attributes?.labCustodyMint,
    user?.id,
    _user?.id,
  ])

  const loadCard = async () => {
    setLoading(true)
    try {
      const res = await getCard(id)
      setCard(res.data.data)
    } catch (error) {
      console.error('Failed to load card:', error)
    } finally {
      setLoading(false)
    }
  }

  const latestOrderForNotif = card?.orders?.[0] ?? null

  useEffect(() => {
    if (!notifEntryTransition || notifTransitionHandledRef.current) return
    if (loading) return

    const minMs = 2000
    const start = notifTransitionStartRef.current ?? Date.now()
    const wait = Math.max(0, minMs - (Date.now() - start))

    const timer = setTimeout(async () => {
      notifTransitionHandledRef.current = true
      setNotifEntryTransition(false)
      navigate(location.pathname, { replace: true, state: {} })

      if (
        card &&
        card.isOwnerView &&
        latestOrderForNotif &&
        orderNeedsOwnerAction(latestOrderForNotif)
      ) {
        if (wallet) {
          try {
            const res = await getNotifications(wallet, 50)
            setFlowModalNotifications(res.data?.data ?? [])
          } catch {
            setFlowModalNotifications([])
          }
        }
        setFlowModalOpen(true)
      } else if (card && card.isOwnerView) {
        requestAnimationFrame(() => {
          document
            .getElementById('card-grading-history')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      }
    }, wait)

    return () => clearTimeout(timer)
  }, [
    notifEntryTransition,
    loading,
    card,
    latestOrderForNotif,
    navigate,
    location.pathname,
    wallet,
  ])

  const loadCardImages = async () => {
    setLoadingImages(true)
    try {
      const res = await getCardImages(card.id)
      setImageUrls({
        frontImageUrl: res.data.data.frontImageUrl,
        backImageUrl: res.data.data.backImageUrl,
      })
    } catch (error) {
      console.error('Failed to load card images:', error)
    } finally {
      setLoadingImages(false)
    }
  }

  const openLabCustodyModal = () => {
    setLabCustodyErr('')
    setLabCustodyWallet((user?.wallet || wallet || '').trim())
    setLabCustodyModalOpen(true)
  }

  const submitLabCustodyRequest = async () => {
    const linked = (user?.wallet || wallet || '').trim().toLowerCase()
    const input = labCustodyWallet.trim()
    const dest = linked || input
    if (!dest || !/^0x[a-fA-F0-9]{40}$/i.test(dest)) {
      setLabCustodyErr('Enter a valid Polygon wallet address (0x…).')
      return
    }
    if (linked && dest.toLowerCase() !== linked) {
      setLabCustodyErr('Use the wallet linked to your account.')
      return
    }
    setLabCustodyBusy(true)
    setLabCustodyErr('')
    try {
      await createLabCustodyClaim({ cardId: card.id, newOwnerWallet: dest })
      setLabCustodyModalOpen(false)
      const res = await getMyLabCustodyClaims()
      const list = res.data?.data || []
      setMyLabCustodyClaim(
        list.find((r) => r.cardId === card.id && r.status === 'PENDING_LAB') || null
      )
    } catch (e) {
      setLabCustodyErr(e.response?.data?.error || e.message || 'Request failed')
    } finally {
      setLabCustodyBusy(false)
    }
  }

  const cancelLabCustodyRequest = async () => {
    if (!myLabCustodyClaim?.id) return
    if (!window.confirm('Cancel this custody request? You can submit a new one later.')) return
    setLabCustodyBusy(true)
    setLabCustodyErr('')
    try {
      await withdrawLabCustodyClaim(myLabCustodyClaim.id)
      setMyLabCustodyClaim(null)
    } catch (e) {
      setLabCustodyErr(e.response?.data?.error || e.message || 'Could not cancel')
    } finally {
      setLabCustodyBusy(false)
    }
  }

  const currentUserId = user?.id || _user?.id
  const isOwner =
    card?.isOwnerView === true ||
    Boolean(currentUserId && card?.owner?.id && currentUserId === card.owner.id)
  const showFullDetails =
    card?.viewerMode === 'owner' ||
    card?.viewerMode === 'staff' ||
    (card?.viewerMode !== 'public' && isOwner)

  // Status color mapping for futuristic theme
  const getStatusStyle = (color) => {
    // M1NT palette: amber (attention), ice (done), violet (on-chain / info), muted (neutral).
    const amber = {
      bg: 'rgba(227, 184, 114, 0.12)',
      border: 'rgba(227, 184, 114, 0.5)',
      text: '#E3B872',
    }
    const ice = {
      bg: 'rgba(143, 216, 230, 0.12)',
      border: 'rgba(143, 216, 230, 0.5)',
      text: '#8FD8E6',
    }
    const violet = {
      bg: 'rgba(180, 156, 245, 0.12)',
      border: 'rgba(180, 156, 245, 0.5)',
      text: '#B49CF5',
    }
    const styles = {
      yellow: amber,
      green: ice,
      blue: violet,
      purple: violet,
      gray: {
        bg: 'rgba(162, 157, 173, 0.12)',
        border: 'rgba(239, 235, 228, 0.14)',
        text: '#A29DAD',
      },
      red: amber,
      orange: amber,
      teal: ice,
    }
    return styles[color] || styles.gray
  }

  if (loading) {
    const previewName = card?.id === id ? getOwnerCardDisplayName(card) : null
    const previewImg =
      card?.id === id
        ? imageUrls.frontImageUrl ||
          card.frontImageUrl ||
          getIPFSUrl(card.metadata?.images?.frontHash) ||
          null
        : null
    return (
      <div className="relative min-h-[60vh]">
        <CardEntryTransition
          visible={notifEntryTransition}
          cardName={previewName}
          imageUrl={previewImg}
        />
        <div className="page-container flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" text="Loading card..." />
        </div>
      </div>
    )
  }

  if (!card) {
    return (
      <div className="relative">
        <CardEntryTransition visible={notifEntryTransition} cardName={null} imageUrl={null} />
        <div className="page-container text-center py-20">
          <h1 className="text-2xl font-bold mb-4 text-white">Card not found</h1>
          <Link to="/owner" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  if (card.status === 'BURNED') {
    return (
      <div className="page-container relative">
        <CardEntryTransition
          visible={notifEntryTransition}
          cardName={getOwnerCardDisplayName(card)}
          imageUrl={null}
        />
        <Link
          to="/owner"
          className="group mb-6 inline-flex min-h-[44px] items-center gap-2 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:text-m1-violet sm:mb-8"
        >
          <FiArrowLeft className="transition-transform group-hover:-translate-x-1" />
          <span>Back to My Cards</span>
        </Link>
        <div className="mx-auto max-w-lg text-center py-16 px-4 rounded-2xl border border-orange-500/25 bg-orange-500/5">
          <div className="mx-auto w-40 h-56 rounded-xl overflow-hidden ring-1 ring-white/10 mb-8">
            <BurnedCardPlaceholder cardName={card.name} />
          </div>
          <h1 className="text-2xl font-bold mb-3 text-white">Burned card</h1>
          <p className="text-gray-400 mb-2">
            This card is no longer in your collection. The on-chain NFT was permanently burned and
            cannot be recovered.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            To grade another card, start a new M1NT submission from the dashboard.
          </p>
          <Link to="/owner" className="btn-primary inline-flex">
            Go to My Cards
          </Link>
        </div>
      </div>
    )
  }

  const status = formatCardStatus(card.status)
  const statusStyle = getStatusStyle(status.color)
  const latestOrder = card.orders?.[0]

  const transitionPreviewUrl =
    imageUrls.frontImageUrl ||
    card.frontImageUrl ||
    getIPFSUrl(card.metadata?.images?.frontHash) ||
    null
  const latestOrderStatusFmt = latestOrder ? formatOrderStatus(latestOrder.status) : null
  const latestOrderStatusLook = latestOrderStatusFmt
    ? getStatusStyle(latestOrderStatusFmt.color)
    : null
  const frontExcluded = isOwnerOriginalExcluded(card, 'front')
  const backExcluded = isOwnerOriginalExcluded(card, 'back')
  // Use signed URLs: from getCardImages, then from getCard (frontImageUrl/backImageUrl), then IPFS or placeholder
  const frontImageUrl = frontExcluded
    ? null
    : normalizeIpfsDisplayUrl(imageUrls.frontImageUrl) ||
      normalizeIpfsDisplayUrl(card.frontImageUrl) ||
      getIPFSUrl(card.metadata?.images?.frontHash) ||
      null
  const imageUrl = frontImageUrl || '/placeholder-card.png'
  const backImageUrl = backExcluded
    ? null
    : normalizeIpfsDisplayUrl(imageUrls.backImageUrl) ||
      (card.backImageUrl != null ? normalizeIpfsDisplayUrl(card.backImageUrl) : null) ||
      getIPFSUrl(card.metadata?.images?.backHash) ||
      null
  const shootMedia = collectCardMedia({
    frontImageUrl,
    backImageUrl,
    quoteVideoUrls: card.quoteVideoUrls,
    ownerStoryMediaUrl: card.ownerStoryMediaUrl,
    ownerDeclarationMediaUrl: card.ownerDeclarationMediaUrl,
    graderInspectionPhotoUrls: card.graderInspectionPhotoUrls,
    graderInspectionVideoUrls: card.graderInspectionVideoUrls,
    labPhotoUrls: card.labPhotoUrls,
    labVideoUrls: card.labVideoUrls,
  })
  const canMint = card.status === 'GRADED' && latestOrder?.completed
  const contractAddress = getMintContractAddressForCard(card)
  const tokenIdForLinks =
    card.tokenId != null && String(card.tokenId).trim() !== ''
      ? card.tokenId
      : latestOrder?.tokenId != null && String(latestOrder.tokenId).trim() !== ''
        ? latestOrder.tokenId
        : null
  const mintTxHash = latestOrder?.nftTxHash || card.txHash
  const openSeaUrl =
    contractAddress && tokenIdForLinks != null
      ? getOpenSeaUrl(contractAddress, tokenIdForLinks)
      : null
  const polygonTokenUrl =
    contractAddress && tokenIdForLinks != null
      ? getPolygonscanTokenUrl(contractAddress, tokenIdForLinks)
      : null
  const polygonTxUrl = mintTxHash ? getPolygonscanUrl(mintTxHash) : null
  /** Prefer NFT token page; fall back to mint transaction if contract missing from API/env. */
  const polygonUrl = polygonTokenUrl || polygonTxUrl

  const isOmNova =
    typeof _user?.name === 'string' &&
    _user.name.trim().toLowerCase() === 'omnova' &&
    typeof wallet === 'string' &&
    typeof _user?.wallet === 'string' &&
    _user.wallet.toLowerCase() === wallet.toLowerCase() &&
    String(card?.owner?.wallet || '').toLowerCase() === wallet.toLowerCase()

  const openTestVisibilityModal = async () => {
    if (!isOmNova) return
    setTestVisibilityError('')
    setTestModalOpen(true)
    try {
      const res = await getLabUsersForVisibility(wallet)
      const users = res.data?.data ?? []
      setLabVisibilityUsers(Array.isArray(users) ? users : [])

      const tv =
        card?.attributes?.testVisibility && typeof card.attributes.testVisibility === 'object'
          ? card.attributes.testVisibility
          : null
      setTestEnabled(!!tv?.enabled)
      const allowed = Array.isArray(tv?.allowedWallets) ? tv.allowedWallets : []
      setTestSelectedWallets(
        allowed.map((w) => (typeof w === 'string' ? w.toLowerCase() : '')).filter(Boolean)
      )
    } catch (e) {
      setTestVisibilityError(
        e.response?.data?.error || e.response?.data?.message || 'Failed to load Lab users.'
      )
    }
  }

  const toggleWallet = (w) => {
    const ww = String(w || '').toLowerCase()
    if (!ww) return
    setTestSelectedWallets((prev) =>
      prev.includes(ww) ? prev.filter((x) => x !== ww) : [...prev, ww]
    )
  }

  const saveTestVisibility = async () => {
    if (!isOmNova) return
    setSavingTestVisibility(true)
    setTestVisibilityError('')
    try {
      await setCardTestVisibility(card.id, wallet, {
        enabled: testEnabled,
        allowedWallets: testSelectedWallets,
      })
      await loadCard()
      setTestModalOpen(false)
    } catch (e) {
      setTestVisibilityError(
        e.response?.data?.error || e.response?.data?.message || 'Failed to save.'
      )
    } finally {
      setSavingTestVisibility(false)
    }
  }

  return (
    <div className="page-container relative">
      <CardEntryTransition
        visible={notifEntryTransition}
        cardName={getOwnerCardDisplayName(card)}
        imageUrl={transitionPreviewUrl}
      />

      {flowModalOpen && isOwner && card && latestOrder && (
        <CardFlowModal
          card={card}
          order={latestOrder}
          imageUrl={transitionPreviewUrl || imageUrl}
          notifications={flowModalNotifications}
          wallet={wallet}
          onClose={() => setFlowModalOpen(false)}
          onActionComplete={loadCard}
        />
      )}

      {testModalOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-gray-800 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-white">Set card as test (Lab visibility)</h3>
                <p className="text-sm text-gray-400 mt-1">
                  When enabled, only the selected Lab wallets can see this card and its data in the
                  Lab app. Everyone else will see nothing.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setTestModalOpen(false)}
                className="text-gray-400 hover:text-white transition"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4">
              {testVisibilityError ? (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
                  {testVisibilityError}
                </div>
              ) : null}

              <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-gray-700 bg-black/20 p-4">
                <input
                  type="checkbox"
                  checked={testEnabled}
                  onChange={(e) => setTestEnabled(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-500 bg-gray-800 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-200">
                  <span className="font-semibold text-white">Enable test visibility</span>
                  <span className="block text-gray-500 mt-1">
                    You must select at least one Lab wallet when enabled.
                  </span>
                </span>
              </label>

              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-gray-300 font-medium">Allowed Lab wallets</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setTestSelectedWallets(
                        labVisibilityUsers
                          .map((u) => String(u.wallet || '').toLowerCase())
                          .filter(Boolean)
                      )
                    }
                    className="text-xs text-cyan-400 hover:underline"
                    disabled={!testEnabled}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setTestSelectedWallets([])}
                    className="text-xs text-gray-400 hover:underline"
                    disabled={!testEnabled}
                  >
                    None
                  </button>
                </div>
              </div>

              <div className="max-h-[320px] overflow-auto rounded-xl border border-gray-700 bg-black/20">
                {labVisibilityUsers.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No Lab users found.</div>
                ) : (
                  <ul className="divide-y divide-gray-800">
                    {labVisibilityUsers.map((u) => {
                      const w = String(u.wallet || '').toLowerCase()
                      const checked = testSelectedWallets.includes(w)
                      return (
                        <li key={u.id} className="p-3">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleWallet(u.wallet)}
                              className="h-4 w-4 rounded border-gray-500 bg-gray-800 text-cyan-500 focus:ring-cyan-500"
                              disabled={!testEnabled}
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm text-white font-medium truncate">
                                  {u.name || '—'}
                                </p>
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-gray-400">
                                  {u.role}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 font-mono break-all">
                                {u.wallet}
                              </p>
                            </div>
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className="p-5 border-t border-gray-800 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setTestModalOpen(false)}
                className="btn-secondary"
                disabled={savingTestVisibility}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveTestVisibility}
                className="btn-primary"
                disabled={savingTestVisibility || (testEnabled && testSelectedWallets.length === 0)}
                title={
                  testEnabled && testSelectedWallets.length === 0
                    ? 'Select at least one Lab wallet'
                    : undefined
                }
              >
                {savingTestVisibility ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <Link
        to={isOwner ? '/owner' : '/'}
        className="group mb-6 inline-flex min-h-[44px] items-center gap-2 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:text-m1-violet sm:mb-8"
      >
        <FiArrowLeft className="transition-transform group-hover:-translate-x-1" />
        <span>{isOwner ? 'Back to Dashboard' : 'Back to Home'}</span>
      </Link>

      <div className="mx-auto w-full max-w-6xl space-y-8 sm:space-y-10">
        <div className="min-w-0 space-y-8">
          {/* Header */}
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium leading-none text-cyan-400">
                <SealEmblem ready className="h-6 w-6 shrink-0" />
                VERIFIED
              </span>
              {card.tokenId && (
                <span className="badge-purple inline-flex items-center gap-1.5 text-xs font-medium leading-none">
                  <PolygonMark className="h-3 w-3 shrink-0" />
                  ON-CHAIN
                </span>
              )}
            </div>
            {/* Card name uses the sans font again (client request). The leading "!" is needed because
                `.m1nt-owner :where(h1, h2)` forces Bodoni on every h1.
                To restore the Bodoni title, use: mb-4 font-m1-display text-[clamp(36px,4.6vw,58px)] font-medium leading-[1.04] tracking-[-0.015em] text-m1-ink sm:font-normal */}
            <h1 className="mb-4 !font-sans text-2xl font-semibold leading-tight tracking-tight text-m1-ink sm:text-3xl">
              {getOwnerCardDisplayName(card)}
            </h1>
            <p className="text-[19px] font-light text-m1-ink-soft">{card.cardType}</p>
            {isOwner && isOmNova && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={openTestVisibilityModal}
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  Set card as test
                </button>
                {card?.attributes?.testVisibility?.enabled ? (
                  <p className="mt-2 text-xs text-cyan-300/80">
                    Test visibility is enabled. Only selected Lab wallets can see this card in the
                    Lab.
                  </p>
                ) : null}
              </div>
            )}
            {tokenIdForLinks != null && (openSeaUrl || polygonUrl) && (
              <div className="mt-6 flex flex-col gap-3 m1nt-plate p-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 sm:p-5">
                <p className="text-[15px] font-light leading-relaxed text-m1-ink-soft sm:min-w-0 sm:flex-1">
                  This NFT is on-chain. Open it on OpenSea or verify the contract on Polygon.
                </p>
                <div className="flex flex-col gap-2.5 sm:flex-row sm:shrink-0">
                  {openSeaUrl ? (
                    <a
                      href={openSeaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary sm:min-w-[200px]"
                    >
                      View on OpenSea{isTestnet() ? ' (Testnet)' : ''}
                      <FiExternalLink size={16} className="shrink-0 opacity-90" />
                    </a>
                  ) : null}
                  {polygonUrl ? (
                    <a
                      href={polygonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary sm:min-w-[200px]"
                    >
                      {polygonTokenUrl ? 'View on Polygon' : 'View mint on Polygon'}
                      <FiExternalLink size={16} className="shrink-0 opacity-90" />
                    </a>
                  ) : null}
                </div>
                {isTestnet() && openSeaUrl ? (
                  <p className="w-full text-[13px] text-m1-muted sm:order-last">
                    OpenSea may take a few minutes to index testnet assets.
                  </p>
                ) : null}
              </div>
            )}
          </div>

          {/* Card preview + info grid — side by side (md+) */}
          {/* Both columns stretch to the same height; the last block on the right grows to fill. */}
          <div className="flex flex-col items-stretch gap-10 md:flex-row md:gap-10 lg:gap-12">
            <div className="mx-auto w-full max-w-[360px] shrink-0 md:mx-0 md:w-[340px] lg:w-[380px] lg:max-w-[380px]">
              <div className="space-y-4 md:sticky md:top-24">
                {loadingImages ? (
                  <div className="flex aspect-[3/4] w-full items-center justify-center border border-m1-line bg-m1-surface">
                    <LoadingSpinner size="md" text="Loading image..." />
                  </div>
                ) : (
                  <Holo3DCard
                    imageUrl={imageUrl}
                    fallbackImageUrl="/placeholder-card.png"
                    alt={getOwnerCardDisplayName(card)}
                    showAfterSplash
                    maxWidthClass="sm:max-w-[380px]"
                    onActivate={() => setCardOpen(true)}
                  />
                )}
                {card.tokenId && (
                  <AuthenticationSeal
                    tokenId={card.tokenId}
                    chainId={VIEW_CHAIN_ID}
                    chainName={getChainName(VIEW_CHAIN_ID)}
                  />
                )}
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-6">
              {/* One certificate plate: four facts separated by hairlines instead of four boxes. */}
              <dl className="m1nt-plate grid grid-cols-2 md:flex-1">
                <div className="border-b border-r border-m1-line-soft p-5 sm:p-7 flex flex-col justify-center">
                  <dt className="text-[14px] text-m1-muted">Grade</dt>
                  <dd className="mt-3">
                    {latestOrder?.gradeOverall != null || latestOrder?.grade != null ? (
                      <span className="font-m1-display text-[48px] leading-none text-m1-violet sm:text-[56px]">
                        {formatOrderFinalGrade(latestOrder)}
                      </span>
                    ) : (
                      <span className="text-[18px] font-light text-m1-muted">—</span>
                    )}
                  </dd>
                </div>
                <div className="border-b border-m1-line-soft p-5 sm:p-7 flex flex-col justify-center">
                  <dt className="text-[14px] text-m1-muted">Owner</dt>
                  <dd className="mt-3">
                    {card.owner?.name ? (
                      <>
                        <p className="text-[18px] text-m1-ink">{card.owner.name}</p>
                        <p className="mt-1 font-m1-mono text-[13px] text-m1-muted">
                          {formatAddress(card.owner?.wallet)}
                        </p>
                      </>
                    ) : (
                      <p className="font-m1-mono text-[15px] text-m1-ink">
                        {formatAddress(card.owner?.wallet)}
                      </p>
                    )}
                  </dd>
                </div>
                <div className="border-r border-m1-line-soft p-5 sm:p-7 flex flex-col justify-center">
                  <dt className="text-[14px] text-m1-muted">Created</dt>
                  <dd className="mt-3 text-[18px] text-m1-ink">{formatDate(card.createdAt)}</dd>
                </div>
                <div className="p-5 sm:p-7 flex flex-col justify-center">
                  <dt className="text-[14px] text-m1-muted">Status</dt>
                  <dd className="mt-3">
                    <span
                      className="inline-block border px-2.5 py-1 font-m1-mono text-[11px] uppercase tracking-[0.12em]"
                      style={{
                        border: `1px solid ${statusStyle.border}`,
                        color: statusStyle.text,
                      }}
                    >
                      {status.label}
                    </span>
                  </dd>
                </div>
              </dl>

              {isOwner &&
                card.status === 'MINTED' &&
                card.tokenId != null &&
                card.attributes?.labCustodyMint === true && (
                  <div className="m1nt-plate mt-4 p-4 sm:p-5">
                    <div className="mb-2 flex items-center gap-2">
                      <div className="icon-container-green flex h-8 w-8 items-center justify-center">
                        <FiSend size={14} className="text-teal-300" />
                      </div>
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-teal-100">
                        Lab custody on-chain
                      </h3>
                    </div>
                    <p className="text-sm text-gray-300">
                      Your NFT is held in M1NT Lab custody on the blockchain. When you are ready,
                      request a transfer to your wallet. The Lab will complete the on-chain transfer
                      after you submit the request.
                    </p>
                    {myLabCustodyClaim ? (
                      <div className="mt-4 space-y-3">
                        <p className="text-sm text-teal-200/90">
                          <span className="font-medium text-white">Request pending.</span>{' '}
                          Destination:{' '}
                          <span className="font-mono text-xs break-all">
                            {myLabCustodyClaim.newOwnerWallet}
                          </span>
                        </p>
                        {myLabCustodyClaim.dueAt && (
                          <p className="text-xs text-gray-500">
                            If the request is not processed in time, it may expire and you can
                            submit again.
                          </p>
                        )}
                        <button
                          type="button"
                          disabled={labCustodyBusy}
                          onClick={cancelLabCustodyRequest}
                          className="rounded-xl border border-white/20 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-white/5 disabled:opacity-50"
                        >
                          Cancel request
                        </button>
                        {labCustodyErr ? (
                          <p className="text-sm text-red-400 break-words">{labCustodyErr}</p>
                        ) : null}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={openLabCustodyModal}
                        className="mt-4 min-h-[44px] w-full border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet sm:w-auto"
                      >
                        Request custody
                      </button>
                    )}
                  </div>
                )}

              {showFullDetails &&
                Array.isArray(card.custodyTransferHistory) &&
                card.custodyTransferHistory.length > 0 && (
                  <div className="m1nt-plate mt-4 p-4 sm:p-5">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="icon-container-cyan flex h-8 w-8 items-center justify-center">
                        <FiRepeat size={14} className="text-cyan-400" />
                      </div>
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300">
                        Custody history
                      </h3>
                    </div>
                    <p className="mb-3 text-xs text-gray-500">
                      In-app custody moves (new holder in M1NT). On-chain completion time is shown
                      when available.
                    </p>
                    <ul className="space-y-3">
                      {card.custodyTransferHistory.map((entry) => {
                        const fromLabel = entry.from
                          ? entry.from.name || formatAddress(entry.from.wallet)
                          : 'Previous holder (not recorded)'
                        const toLabel = entry.to
                          ? entry.to.name || formatAddress(entry.to.wallet)
                          : '—'
                        return (
                          <li
                            key={entry.id}
                            className="rounded-xl border border-white/10 bg-black/20 px-3 py-3 text-sm"
                          >
                            <p className="text-xs font-medium text-gray-500">
                              {entry.transferredAt
                                ? `Transfer completed · ${formatDate(entry.transferredAt)}`
                                : 'Transfer recorded'}
                            </p>
                            <p className="mt-1.5 text-gray-300">
                              <span className="text-gray-500">From </span>
                              <span className="font-medium text-white">{fromLabel}</span>
                            </p>
                            <p className="mt-0.5 text-gray-300">
                              <span className="text-gray-500">To </span>
                              <span className="font-medium text-cyan-200">{toLabel}</span>
                            </p>
                            {entry.txHash && getPolygonscanUrl(entry.txHash) && (
                              <a
                                href={getPolygonscanUrl(entry.txHash)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-cyan-400 hover:text-cyan-300"
                              >
                                <FiExternalLink size={12} />
                                View transfer transaction
                              </a>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}

              {/* Actions - Ready to Mint */}
              {isOwner && canMint && (
                <div className="m1nt-plate m1nt-plate--amber p-5 sm:p-7">
                  <PlateHeader
                    icon={<i className="ri-flashlight-line" />}
                    eyebrow="Grading complete"
                    title="Ready to mint"
                  />
                  <p className="max-w-[40em] text-[16px] font-light leading-relaxed text-m1-ink-soft">
                    Now that grading is complete, M1NT will receive your card and mint it as an NFT
                    for you, creating permanent, tamper-proof on-chain verification.
                  </p>
                </div>
              )}

              {/* NFT Info */}
              {card.tokenId && (
                <div
                  id={ON_CHAIN_SECTION_ID}
                  className="m1nt-plate flex scroll-mt-20 flex-col p-5 sm:p-7 md:flex-1"
                >
                  <PlateHeader icon={<FiCpu />} eyebrow="On-chain record" title="NFT Information" />
                  <div className="flex flex-1 flex-col justify-evenly">
                    {contractAddress && (
                      <div className="flex justify-between items-center py-3 border-b border-white/5">
                        <span className="text-gray-400 font-medium">Contract</span>
                        {polygonTokenUrl ? (
                          <a
                            href={polygonTokenUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-cyan-400 hover:text-cyan-300 flex items-center gap-2 transition-all duration-300 hover:gap-3"
                          >
                            {formatAddress(contractAddress)} <FiExternalLink size={14} />
                          </a>
                        ) : (
                          <span className="font-mono text-m1-ink">
                            {formatAddress(contractAddress)}
                          </span>
                        )}
                      </div>
                    )}
                    {mintTxHash && (
                      <div className="flex justify-between items-center py-3 border-b border-white/5">
                        <span className="text-gray-400 font-medium">Transaction</span>
                        <a
                          href={getPolygonscanUrl(mintTxHash)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-cyan-400 hover:text-cyan-300 flex items-center gap-2 transition-all duration-300 hover:gap-3"
                        >
                          {String(mintTxHash).slice(0, 10)}... <FiExternalLink size={14} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Grading History — page-specific layout (GradingHistoryTimeline) */}
          {showFullDetails && card.orders && card.orders.length > 0 && latestOrder && (
            <div id="card-grading-history" className="m1nt-plate scroll-mt-24 p-5 sm:p-7">
              <PlateHeader
                icon={<FiStar />}
                eyebrow="Progress across your grading journey"
                title="Grading History"
              />

              <div className="min-w-0">
                <div className="overflow-hidden rounded-[12px] border border-white/[0.08] bg-white/[0.02]">
                  <div className="p-4 sm:p-5">
                    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 sm:gap-x-4">
                        <h4 className="text-sm font-semibold text-gray-400">Progress</h4>
                        {latestOrderStatusFmt && latestOrderStatusLook && (
                          <span
                            className="inline-flex px-3 py-1.5 text-xs font-bold tracking-wide rounded-full"
                            style={{
                              background: latestOrderStatusLook.bg,
                              border: `1px solid ${latestOrderStatusLook.border}`,
                              color: latestOrderStatusLook.text,
                            }}
                          >
                            {latestOrderStatusFmt.label}
                          </span>
                        )}
                        <span
                          className="hidden h-4 w-px shrink-0 bg-white/15 sm:block"
                          aria-hidden
                        />
                        <p className="min-w-0 text-sm text-gray-400">
                          <span className="text-gray-500">Grader:</span>{' '}
                          <span className="font-mono text-white">
                            {formatAddress(latestOrder.grader?.wallet)}
                          </span>
                        </p>
                        {(latestOrder.priceUSD != null || latestOrder.price != null) && (
                          <>
                            <span className="h-4 w-px shrink-0 bg-white/15" aria-hidden />
                            <p className="text-sm text-gray-400">
                              <span className="text-gray-500">Price:</span>{' '}
                              {latestOrder.priceUSD != null ? (
                                <span className="font-medium text-cyan-400">
                                  ${formatCurrency(latestOrder.priceUSD, 2)} USD
                                </span>
                              ) : (
                                <span className="font-medium text-cyan-400">
                                  {formatCurrency(latestOrder.price)} MATIC
                                </span>
                              )}
                            </p>
                          </>
                        )}
                      </div>
                      {(latestOrder.grade != null || latestOrder.gradeOverall != null) && (
                        <div className="grade-badge w-fit shrink-0 text-sm px-4 py-2 sm:px-5 sm:py-2.5 sm:ml-0">
                          {formatOrderFinalGrade(latestOrder)}
                        </div>
                      )}
                    </div>

                    <div className="border-t border-white/[0.06] pt-4">
                      <GradingHistoryTimeline
                        currentStep={getFlowConfig(latestOrder.status).step}
                        dates={buildTimelineDates(latestOrder)}
                        steps={TIMELINE_STEPS}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attributes — hide file keys already shown in the gallery */}
          {showFullDetails &&
            card.attributes &&
            Object.entries(card.attributes).filter(([k]) => !isHiddenAttributeKey(k)).length >
              0 && (
              <div className="m1nt-plate p-5 sm:p-7">
                <PlateHeader icon={<FiShield />} eyebrow="Card record" title="Attributes" />
                <CardAttributesLedger
                  entries={Object.entries(card.attributes).filter(
                    ([key]) => !isHiddenAttributeKey(key)
                  )}
                />
              </div>
            )}
        </div>
      </div>

      {labCustodyModalOpen && card?.id && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="presentation"
          onClick={() => !labCustodyBusy && setLabCustodyModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="lab-custody-modal-title"
            className="max-w-md w-full rounded-2xl border border-white/10 bg-[#0f172a] p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="lab-custody-modal-title" className="text-lg font-bold text-white mb-2">
              Request custody
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              Enter the Polygon wallet that should receive this NFT. If your account already has a
              linked wallet, it must match.
            </p>
            {user?.wallet || wallet ? (
              <p className="text-xs text-teal-200/90 font-mono break-all mb-3">
                Linked wallet: {user?.wallet || wallet}
              </p>
            ) : (
              <label className="block">
                <span className="text-xs text-gray-500 uppercase tracking-wide">
                  Wallet address
                </span>
                <input
                  type="text"
                  value={labCustodyWallet}
                  onChange={(e) => setLabCustodyWallet(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white font-mono"
                  placeholder="0x…"
                  autoComplete="off"
                />
              </label>
            )}
            {labCustodyErr ? (
              <p className="mt-3 text-sm text-red-400 break-words">{labCustodyErr}</p>
            ) : null}
            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                disabled={labCustodyBusy}
                onClick={() => setLabCustodyModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm text-gray-300 hover:bg-white/5"
              >
                Close
              </button>
              <button
                type="button"
                disabled={labCustodyBusy}
                onClick={submitLabCustodyRequest}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-500 disabled:opacity-50"
              >
                {labCustodyBusy ? 'Submitting…' : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFullDetails && (
        <div className="mt-10 w-full sm:mt-12">
          <CardRecordSections card={card} />
        </div>
      )}

      {showFullDetails && (
        <div className="relative left-1/2 mt-16 w-screen -translate-x-1/2 bg-m1-ground">
          <ShootSection
            images={shootMedia.images.map((item) => item.url)}
            labels={shootMedia.images.map((item) => item.label)}
            heroImage={frontImageUrl}
            cardName={getOwnerCardDisplayName(card)}
          />
          <VideoSection videos={shootMedia.videos} />
          <div className="mx-auto mt-10 w-full max-w-[1200px] px-[var(--gutter)] sm:mt-12">
            <CardDetailMediaGallery
              quoteDocumentUrls={card.quoteDocumentUrls}
              ownerStoryMediaUrl={card.ownerStoryMediaUrl}
              ownerDeclarationMediaUrl={card.ownerDeclarationMediaUrl}
              graderInspectionDocumentUrls={card.graderInspectionDocumentUrls}
              graderReferenceLinks={
                card.graderReferenceLinks ?? card.graderRecord?.referenceLinks ?? []
              }
              labCertificateUrls={card.labCertificateUrls}
              labDocumentUrls={card.labDocumentUrls}
              labNewsLinks={card.labNewsLinks ?? card.labRecord?.newsLinks ?? []}
            />
          </div>
        </div>
      )}

      <MediaLightbox
        media={
          cardOpen
            ? {
                kind: 'image',
                url: imageUrl,
                alt: getOwnerCardDisplayName(card),
                backUrl: backImageUrl || undefined,
              }
            : null
        }
        onClose={() => setCardOpen(false)}
        badge={card.tokenId ? <CardSealBadge /> : undefined}
        legend={card.tokenId ? <CardAuthLegend /> : undefined}
        immersive
        tilt
      />
    </div>
  )
}

export default function CardDetail() {
  const { id } = useParams()
  return (
    <RouteErrorBoundary key={id}>
      <ImageLightboxProvider>
        <CardDetailContent id={id} />
      </ImageLightboxProvider>
    </RouteErrorBoundary>
  )
}
